import classNames from 'classnames'
import { endOfDay } from 'date-fns'
import { FC, ReactNode, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CSSTransition } from 'react-transition-group'

import { CurrencyExchange } from '@mui/icons-material'
import { Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material'

import { useGetTrackedEventQuery } from '../../../../../api/core'
import { Analyst } from '../../../../../components/Analyst/Analyst'
import GRCircularProgress from '../../../../../components/GRCircularProgress/GRCircularProgress'
import GalleryCarousel, { GalleryCarouselImageListingType, GalleryCarouselImagePreviewMode } from '../../../../../components/GalleryCarousel/GalleryCarousel'
import { HtmlContentParser } from '../../../../../components/HtmlContentParser/HtmlContentParser'
import { ModalMode, liveEventReviewModalParserOptions } from '../../../../../components/LiveEventModalLink/LiveEventModalLink'
import { TranslationToggle } from '../../../../../components/TranslationToggle/TranslationToggle'
import { useContentTranslation } from '../../../../../hooks/translationHooks'
import { useError } from '../../../../../hooks/useError'
import { useInternalAccessCheck } from '../../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { calculateEventPerformanceChanges, getEventKpiPeriodExplanation } from '../../../helpers/calculateEventPerformanceChanges'
import { liveEventPriceRange } from '../../../hooks/liveEventPriceRange'
import { useLiveEventDialogDescriptionTabAnalyticsEvents } from '../../../hooks/useLiveEventsTrackerAnalyticsEvents'
import { useDefaultLiveEvents } from '../../../hooks/useTrackedGamesEvents'
import { TrackedGame } from '../../../types/TrackedGame'
import { Duration } from '../../../types/TrackingEvents'
import CurrencyDialog from '../../CurrencyDialog/CurrencyDialog'
import { EventKpiExplanationCallout } from '../../EventKpiExplanationCallout/EventKpiExplanationCallout'
import { LiveEventMotivationsSection } from '../../LiveEventMotivationsSection/LiveEventMotivationsSection'
import LiveEventPricePoints from '../../LiveEventPricePoints/LiveEventPricePoints'
import LiveEventsSecondaryTaxonomyTagList from '../../LiveEventsSecondaryTaxonomyTagList/LiveEventsSecondaryTaxonomyTagList'
import { EventHeader } from '../EventHeader/EventHeader'
import './DescriptionTab.scss'

type CurrencyButtonProps = {
  currencies: any[] | undefined
  onOpenDialog: () => void
  t: (key: string) => string
}

export const CurrencyButton: FC<CurrencyButtonProps> = ({ currencies, onOpenDialog, t }) => {
  if (!currencies || currencies.length === 0) return null

  return (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <Button variant="contained" color="primary" startIcon={<CurrencyExchange />} onClick={onOpenDialog} size="small">
        {t('live-events:view_all_currencies')}
      </Button>
    </Box>
  )
}

export type DescriptionTabImageListingType = 'carousel' | 'thumbnails'
/**
 * Component representing event dialog Description tab
 */
type DescriptionTabProps = {
  currentTrackedGame?: TrackedGame
  eventId?: string
  eventTypeId?: string
  eventTypeName?: string
  children?: ReactNode
  screenshotIndex?: number
  duration?: Duration
  onScreenshotIndexChange: (screenshotIndex: number) => void
  onScrollParentTop: () => void
  onEventHighlighted?: (eventId: string) => void
}

export const DescriptionTab: FC<DescriptionTabProps> = ({
  currentTrackedGame,
  eventId,
  eventTypeId,
  eventTypeName,
  screenshotIndex = 0,
  duration,
  onScreenshotIndexChange,
  onScrollParentTop,
  onEventHighlighted,
}) => {
  const eventQuery = useGetTrackedEventQuery(eventId as string, { skip: !eventId })
  useError({ error: eventQuery.error })

  const { t } = useTranslation()
  const theme = useTheme()
  const { handleTrackImageListingTypeChangeEvent } = useLiveEventDialogDescriptionTabAnalyticsEvents()
  const userLanguage = useCurrentUserLanguage()
  const isFullscreenAvailable = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true })
  const [imageListingType, setImageListingType] = useState<GalleryCarouselImageListingType>('carousel')
  const [imagePreviewMode, setImagePreviewMode] = useState<GalleryCarouselImagePreviewMode>('default')
  const [flexDirection, setFlexDirection] = useState<'row' | 'column-reverse' | 'column'>(isFullscreenAvailable ? 'row' : 'column')
  const [currenciesDialogOpen, setCurrenciesDialogOpen] = useState(false)
  const { isTranslating, translatedContent, translationError, handleTranslateClick, displayContent } = useContentTranslation()
  const isLoading = eventQuery.isLoading || eventQuery.isFetching
  const isImagePreviewFullscreenMode = imagePreviewMode === 'fullscreen'
  const priceRange = liveEventPriceRange(eventQuery.data?.event.pricePoints, currentTrackedGame?.game?.currencies)
  const internalUser = useInternalAccessCheck()
  const showWipMessage = eventQuery.data?.event.active === false && !internalUser
  const isMobileGame = currentTrackedGame?.game.isMobileGame() ?? false

  const gameId = currentTrackedGame?.game.id
  const {
    events,
    performanceChanges: performanceChangesByGame,
    performanceValues: performanceValuesByGame,
    isLoading: isLiveEventsLoading,
  } = useDefaultLiveEvents({
    trackedGameIds: gameId ? [gameId] : [],
  })

  const matchedTrackingEvent = useMemo(() => {
    if (!gameId || !eventId || !events[gameId]) return undefined
    return events[gameId].find((e) => e.eventId === eventId)
  }, [gameId, eventId, events])

  const kpiDates = useMemo(() => {
    const primaryStart = duration?.start ?? matchedTrackingEvent?.start
    const primaryEnd = duration?.end ?? matchedTrackingEvent?.end
    if (!primaryStart || !primaryEnd) return undefined
    const explanation = getEventKpiPeriodExplanation({ eventStart: primaryStart, eventEnd: primaryEnd })
    return { primaryStart, primaryEnd, explanation }
  }, [matchedTrackingEvent, duration])

  const eventKpiSummary = useMemo(() => {
    if (!matchedTrackingEvent || !kpiDates?.explanation) return undefined

    const gid = matchedTrackingEvent.gameId
    const { primaryStart, primaryEnd, explanation } = kpiDates
    const calculated = calculateEventPerformanceChanges({
      eventStart: primaryStart,
      eventEnd: primaryEnd,
      performanceChanges: performanceChangesByGame?.[gid],
      performanceValues: performanceValuesByGame?.[gid],
    })
    const hasRev = calculated?.REVENUE_DAY_1_CHANGE !== undefined
    const hasDl = calculated?.DOWNLOAD_DAY_1_CHANGE !== undefined
    const hasDau = calculated?.DAU_DAY_1_CHANGE !== undefined
    if (!calculated || (!hasRev && !hasDl && !hasDau)) return undefined
    return {
      revenueChange: calculated.REVENUE_DAY_1_CHANGE,
      downloadChange: calculated.DOWNLOAD_DAY_1_CHANGE,
      dauChange: calculated.DAU_DAY_1_CHANGE,
      explanation,
      eventStart: primaryStart,
      eventEnd: primaryEnd,
    }
  }, [matchedTrackingEvent, kpiDates, performanceChangesByGame, performanceValuesByGame])

  const completedEventNoDataRange = useMemo(() => {
    if (eventKpiSummary || !kpiDates) return undefined
    const { primaryEnd, primaryStart, explanation } = kpiDates
    if (endOfDay(new Date(primaryEnd)).getTime() >= Date.now()) return undefined
    return { eventStart: primaryStart, eventEnd: primaryEnd, explanation }
  }, [eventKpiSummary, kpiDates])

  const ongoingEventCallout = useMemo(() => {
    if (eventKpiSummary || !kpiDates) return undefined
    const { primaryEnd, primaryStart, explanation } = kpiDates
    const now = Date.now()
    const hasStarted = new Date(primaryStart).getTime() <= now
    const hasEnded = endOfDay(new Date(primaryEnd)).getTime() < now
    if (!hasStarted || hasEnded) return undefined
    const calloutExplanation = explanation?.variant === 'multi_day_average' ? explanation : undefined
    return { eventStart: primaryStart, eventEnd: primaryEnd, explanation: calloutExplanation }
  }, [eventKpiSummary, kpiDates])

  const screenshots = useMemo(() => {
    if (!eventQuery.data?.event.screenshotUrls) return []

    return eventQuery.data?.event.screenshotUrls?.filter((url) => url)
  }, [eventQuery.data?.event.screenshotUrls])
  const descriptionRawHtml = eventQuery.data?.event.comment?.content?.comment?.[userLanguage]
    ? eventQuery.data?.event.comment?.content?.comment?.[userLanguage]
    : eventQuery.data?.event.comment?.content?.comment?.['en']
  const blueprintDescription = eventQuery.data?.event.archetypeEvolutionData?.descriptions?.[userLanguage]
    ? eventQuery.data?.event.archetypeEvolutionData?.descriptions?.[userLanguage]
    : eventQuery.data?.event.archetypeEvolutionData?.descriptions?.['en']
  const eventName = eventQuery.data?.event.name
  const analyst = eventQuery.data?.analyst

  const handleImageListingTypeChange = (newType: GalleryCarouselImageListingType) => {
    setImageListingType(newType)
    if (newType === 'thumbnails') {
      onScrollParentTop()
    }

    handleTrackImageListingTypeChangeEvent({
      imageListingType,
      trackedGame: currentTrackedGame,
      eventId,
      eventName,
      eventTypeId,
      eventTypeName,
    })
  }

  const thumbnailGridSize = useMemo(() => {
    const count = screenshots.length
    if (count === 1) return 6
    if (count <= 4) return isImagePreviewFullscreenMode ? 4 : 6
    if (count <= 9) return isImagePreviewFullscreenMode ? 3 : 4
    return 3
  }, [isImagePreviewFullscreenMode, screenshots.length])

  const descriptionItemClasses = classNames('DescriptionTab__description', {
    'DescriptionTab__description--fullscreen': isImagePreviewFullscreenMode,
  })

  const cardContent = () => {
    return (
      <Card>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <EventHeader
              eventId={eventId}
              eventTypeId={eventTypeId}
              trackedGameId={currentTrackedGame?.game.id}
              duration={duration}
              priceRange={priceRange}
              onEventHighlighted={onEventHighlighted}
            />
          </Box>
          {isMobileGame &&
            (isLiveEventsLoading && !eventKpiSummary ? (
              <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : eventKpiSummary ? (
              <EventKpiExplanationCallout
                explanation={eventKpiSummary.explanation}
                revenueChange={eventKpiSummary.revenueChange}
                downloadChange={eventKpiSummary.downloadChange}
                dauChange={eventKpiSummary.dauChange}
                eventStart={eventKpiSummary.eventStart}
                eventEnd={eventKpiSummary.eventEnd}
              />
            ) : completedEventNoDataRange ? (
              <EventKpiExplanationCallout
                eventStart={completedEventNoDataRange.eventStart}
                eventEnd={completedEventNoDataRange.eventEnd}
                explanation={completedEventNoDataRange.explanation}
              />
            ) : ongoingEventCallout ? (
              <EventKpiExplanationCallout
                eventStart={ongoingEventCallout.eventStart}
                eventEnd={ongoingEventCallout.eventEnd}
                explanation={ongoingEventCallout.explanation}
              />
            ) : null)}
          {eventQuery?.data?.event.tags && eventQuery.data.event.tags.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} style={{ marginTop: '0' }}>
                {t('live-events:live_events_taxonomy')}
              </Divider>

              <Box mb={2} display="flex" justifyContent="center" flexWrap="wrap">
                <LiveEventsSecondaryTaxonomyTagList tags={eventQuery.data.event.tags || []} />
              </Box>
            </>
          )}

          {eventQuery.data && eventQuery.data.motivations && (
            <LiveEventMotivationsSection motivations={eventQuery.data.motivations.motivations} archetypes={eventQuery.data.motivations.archetypes} />
          )}

          {/* TODO: Remove internal user check when we want to release mechanic changes to all users */}
          {internalUser && blueprintDescription && blueprintDescription.trim().length > 0 && (
            <>
              <Divider sx={{ mt: 3.5, mb: 2 }}>{t('live-events:change_in_this_version')}</Divider>
              <Typography variant="body1" component="div" fontStyle="italic" sx={{ mb: 2 }}>
                <HtmlContentParser
                  rawHtml={blueprintDescription}
                  parserOptions={liveEventReviewModalParserOptions(currentTrackedGame?.game.id || '', { liveEventModalMode: ModalMode.Replace })}
                />
              </Typography>
            </>
          )}

          <Divider sx={{ my: 2 }} style={{ marginTop: '0' }}>
            <Trans i18nKey="common:description" />
          </Divider>

          {analyst && (
            <Box mb={2}>
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid item>
                  <Analyst name={analyst?.name} title={analyst?.title} picture={analyst?.picture} />
                </Grid>
                {!showWipMessage && descriptionRawHtml && userLanguage !== 'en' && (
                  <Grid item>
                    <TranslationToggle
                      isTranslating={isTranslating}
                      hasTranslation={!!translatedContent}
                      error={translationError}
                      onToggle={() => handleTranslateClick(descriptionRawHtml, userLanguage)}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {showWipMessage ? (
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {t('live-events:event_wip_tooltip')}
            </Typography>
          ) : (
            descriptionRawHtml &&
            descriptionRawHtml.length > 0 && (
              <Typography variant="body1" component="div">
                <HtmlContentParser
                  rawHtml={displayContent(descriptionRawHtml)}
                  parserOptions={liveEventReviewModalParserOptions(currentTrackedGame?.game.id || '', { liveEventModalMode: ModalMode.Replace })}
                />
              </Typography>
            )
          )}

          {currentTrackedGame && eventQuery?.data?.event.pricePoints && eventQuery.data.event.pricePoints.length > 0 && (
            <>
              <Divider sx={{ mt: 3.5, mb: 2 }}>{t('live-events:price_points')}</Divider>

              <LiveEventPricePoints pricePoints={eventQuery.data.event.pricePoints} currencies={currentTrackedGame?.game?.currencies} />

              <CurrencyButton currencies={currentTrackedGame?.game?.currencies} onOpenDialog={() => setCurrenciesDialogOpen(true)} t={t} />
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  return isLoading ? (
    <GRCircularProgress />
  ) : screenshots.length > 0 ? (
    <>
      <Grid className="DescriptionTab" container columnSpacing={2} flexDirection={flexDirection}>
        <Grid item flexShrink={1} className={descriptionItemClasses} mb={2}>
          {cardContent()}
        </Grid>

        <CSSTransition
          in={imagePreviewMode === 'default'}
          timeout={300}
          classNames="animate"
          onEnter={() => setFlexDirection('row')}
          onExited={() => setFlexDirection('column-reverse')}
          appear
        >
          <Grid item className={imagePreviewMode === 'default' ? 'DescriptionTab__gridItemImage' : 'DescriptionTab__fullscreenItemImage'}>
            <div className={imageListingType === 'carousel' && imagePreviewMode === 'default' ? 'DescriptionLeftSticky' : ''}>
              <GalleryCarousel
                screenshots={screenshots}
                screenshotIndex={screenshotIndex}
                carouselImageCount={1}
                thumbnailGridSize={thumbnailGridSize}
                imageListingType={imageListingType}
                imagePreviewMode={imagePreviewMode}
                onImageListingTypeChange={(newType: GalleryCarouselImageListingType) => handleImageListingTypeChange(newType)}
                onImagePreviewModeChange={(newMode: GalleryCarouselImagePreviewMode) => setImagePreviewMode(newMode)}
                filename={`let-${currentTrackedGame?.game.name}_${eventTypeName}`
                  .toLocaleLowerCase()
                  .trim()
                  .replace(/ /g, '_') // Replaces spaces with underscores
                  .replace(/[^a-zA-Z0-9_-]/g, '')} // Removes all other special characters except _ and -
              />
            </div>
          </Grid>
        </CSSTransition>
      </Grid>
      <CurrencyDialog open={currenciesDialogOpen} onClose={() => setCurrenciesDialogOpen(false)} currencies={currentTrackedGame?.game?.currencies} />
    </>
  ) : (
    <>
      {cardContent()}
      <CurrencyDialog open={currenciesDialogOpen} onClose={() => setCurrenciesDialogOpen(false)} currencies={currentTrackedGame?.game?.currencies} />
    </>
  )
}
